// Side threads: a read-only side conversation forked off one primary-thread
// message (specifications/side-threads.md). Mirrors chat.ts's conventions —
// typed DTOs, a resource hook per surface, realtime wired into refetch — but
// skips the incremental-merge machinery `useChatMessages` earns: a side
// thread's traffic is light enough that a full refetch on every event is
// cheap, and the prototype has no pagination to preserve across one.

import { api, type ApiError } from "./client";
import type { ApiMessageAttachment } from "./types";
import { useResource } from "./useResource";
import { useRealtimeScope } from "../realtime/useRealtimeScope";

// DTOs (snake_case) — mirror the side-thread routes' JSON. Local to this file
// per the runners.ts/diffComments.ts convention: specific to this surface,
// not worth widening the shared types.ts for.
export type ApiSideThreadAnchor = {
  readonly body: string | null;
  readonly public_id: string;
};

export type ApiSideThreadLastMessage = {
  readonly body: string | null;
  readonly role: "agent" | "user";
};

// One row of GET /tasks/:id/threads. No `status` — a side thread has exactly
// one state, open, forever (side-threads.md §Data model); `last_activity_at`
// ordering is the whole of "tracking" one.
export type ApiSideThread = {
  readonly anchor: ApiSideThreadAnchor;
  readonly archived_at: string | null;
  readonly created_at: string;
  readonly last_activity_at: string;
  readonly last_message: ApiSideThreadLastMessage | null;
  readonly public_id: string;
  readonly title: string | null;
  /** Non-null while a forked turn is out — the panel's working cue. */
  readonly turn_started_at: string | null;
};

// A side thread's own message (GET/POST .../messages) — no system rows:
// `chat_side_thread_messages` only ever carries 'user' | 'agent'
// (side-threads.md §Data model). `body` is nullable — an attachment-only or
// `tool_use` message carries no text.
export type ApiSideThreadMessage = {
  readonly attachments: ReadonlyArray<ApiMessageAttachment>;
  readonly author_user_id: number | null;
  readonly body: string | null;
  /** 'tool_use' rows render as a quiet tool line, not a bubble. */
  readonly kind: "notice" | "text" | "tool_use";
  readonly occurred_at: string;
  readonly public_id: string;
  readonly role: "agent" | "user";
  readonly sequence: number;
  readonly tool_call: {
    readonly argument_summary: string | null;
    readonly duration_ms: number | null;
    readonly name: string;
    readonly status: "cancelled" | "failed" | "running" | "succeeded";
  } | null;
};

export type CreatedSideThread = {
  readonly message: ApiSideThreadMessage;
  readonly thread: {
    readonly anchor_message_public_id: string;
    readonly created_at: string;
    readonly public_id: string;
  };
};

export type UseSideThreads = {
  readonly error: ApiError | undefined;
  readonly loading: boolean;
  readonly refetch: () => void;
  readonly threads: ReadonlyArray<ApiSideThread>;
};

// The threads drawer's data: every side thread on the task. The server's own
// order (`last_activity_at desc`, side-threads.md §API) isn't what's shown —
// `SideThreadsList` sorts chronologically off `created_at` itself (§UX), so
// this hook just hands back whatever it fetched. Both realtime kinds refetch
// the whole list rather than patching one row in place — `created` adds a
// row, `message.appended` moves a preview — and a list this short-lived
// isn't worth a merge function for (side-threads.md §Prototype scope).
export const useSideThreads = (taskId: string | null): UseSideThreads => {
  const { data, error, loading, refetch } = useResource<{
    threads: ReadonlyArray<ApiSideThread>;
  }>(taskId ? `/tasks/${taskId}/threads` : null);

  useRealtimeScope(taskId ? `task:${taskId}` : null, (event) => {
    if (
      event.kind === "chat.side_thread.created" ||
      event.kind === "chat.side_thread.message.appended" ||
      event.kind === "chat.side_thread.updated" ||
      event.kind === "resync"
    ) {
      refetch();
    }
  });

  return { error, loading, refetch, threads: data?.threads ?? [] };
};

export type UseSideThreadMessages = {
  readonly error: ApiError | undefined;
  readonly loading: boolean;
  readonly messages: ReadonlyArray<ApiSideThreadMessage>;
  readonly refetch: () => void;
};

// One side thread's transcript. `threadId === null` is the "start a side
// thread" draft, before the first message has been sent — there is nothing to
// read yet, so the fetch is skipped.
export const useSideThreadMessages = (
  taskId: string | null,
  threadId: string | null,
): UseSideThreadMessages => {
  const path =
    taskId && threadId ? `/tasks/${taskId}/threads/${threadId}/messages` : null;
  const { data, error, loading, refetch } = useResource<{
    messages: ReadonlyArray<ApiSideThreadMessage>;
  }>(path, { limit: 500 });

  useRealtimeScope(taskId ? `task:${taskId}` : null, (event) => {
    if (event.kind === "resync") {
      refetch();
      return;
    }
    if (event.kind !== "chat.side_thread.message.appended") {
      return;
    }
    const summary = (event.summary ?? {}) as {
      side_thread_public_id?: unknown;
    };
    if (summary.side_thread_public_id === threadId) {
      refetch();
    }
  });

  return { error, loading, messages: data?.messages ?? [], refetch };
};

// Fork a side thread off one primary-thread message, with its first message
// (side-threads.md §API). `attachmentIds` are uploads already claimed via
// `uploadAttachment` (`attachments.ts`) — same two-phase dance the primary
// composer uses. The agent's reply follows over realtime — a real forked
// turn when there's a sandbox to run it against, a stub ~900ms later
// otherwise (side-threads.md §Agent execution).
export const createSideThread = (
  taskId: string,
  anchorMessagePublicId: string,
  body: string,
  attachmentIds: ReadonlyArray<string> = [],
): Promise<CreatedSideThread> =>
  api.post<CreatedSideThread>(`/tasks/${taskId}/threads`, {
    anchor_message_public_id: anchorMessagePublicId,
    body,
    ...(attachmentIds.length > 0 ? { attachment_ids: attachmentIds } : {}),
  });

// Stop the turn a side thread is waiting on (best-effort).
export const interruptSideThread = (
  taskId: string,
  threadId: string,
): Promise<{ delivered: boolean }> =>
  api.post(`/tasks/${taskId}/threads/${threadId}/interrupt`);

// Rename (`title`, null clears it) or archive/unarchive (`archived`).
export const patchSideThread = (
  taskId: string,
  threadId: string,
  patch: { archived?: boolean; title?: string | null },
): Promise<{
  thread: {
    archived_at: string | null;
    public_id: string;
    title: string | null;
  };
}> => api.patch(`/tasks/${taskId}/threads/${threadId}`, patch);

export const postSideThreadMessage = (
  taskId: string,
  threadId: string,
  body: string,
  attachmentIds: ReadonlyArray<string> = [],
): Promise<{ message: ApiSideThreadMessage }> =>
  api.post(`/tasks/${taskId}/threads/${threadId}/messages`, {
    body,
    ...(attachmentIds.length > 0 ? { attachment_ids: attachmentIds } : {}),
  });
